import { useStepProgress } from '@/hooks/useStepProgress';

interface StepMeta {
  id: string;
  num: string;
  label: string;
}

const STEPS: StepMeta[] = [
  { id: 'step-01', num: '01', label: 'Ingest' },
  { id: 'step-02', num: '02', label: 'Evaluate' },
  { id: 'step-03', num: '03', label: 'Decision' },
  { id: 'step-04', num: '04', label: 'Direction' },
  { id: 'step-05', num: '05', label: 'Risk Gate' },
  { id: 'step-06', num: '06', label: 'Execute' },
  { id: 'step-07', num: '07', label: 'Manage' },
  { id: 'step-08', num: '08', label: 'Close' },
  { id: 'step-09', num: '09', label: 'Record' },
];

const STEP_IDS = STEPS.map((step) => step.id);
const LAST = STEPS.length - 1;
const NODE = 24;
const GAP = 16;
const ADVANCE = NODE + GAP;

function scrollToStep(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 96;
  window.scrollTo({ top, behavior: 'smooth' });
}

function StepTracker() {
  const { active, inRange } = useStepProgress(STEP_IDS);
  const current = STEPS[active];
  const progress = ((active + 1) / STEPS.length) * 100;

  return (
    <>
      {/* Desktop rail */}
      <nav
        aria-label="Step progress"
        className={`pointer-events-none fixed right-4 top-1/2 z-40 hidden -translate-y-1/2 transition-all duration-500 lg:block ${
          inRange ? 'translate-x-0 opacity-100' : 'translate-x-3 opacity-0'
        }`}
      >
        <div className="relative flex flex-col items-center gap-4">
          <span
            className="absolute left-1/2 top-3 bottom-3 w-px -translate-x-1/2 bg-background-300"
            aria-hidden
          />
          <span
            className="absolute left-1/2 top-3 w-px -translate-x-1/2 bg-primary-500 transition-[height] duration-500 ease-out"
            style={{ height: `${active * ADVANCE}px` }}
            aria-hidden
          />

          {STEPS.map((step, index) => {
            const isActive = index === active;
            const isDone = index < active;
            const state = isActive
              ? 'border-primary-500 bg-primary-500 text-background-50 ring-4 ring-primary-500/15'
              : isDone
                ? 'border-primary-300 bg-primary-50 text-primary-600'
                : 'border-background-300 bg-background-50 text-foreground-400';

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => scrollToStep(step.id)}
                aria-label={`Step ${step.num} · ${step.label}`}
                aria-current={isActive ? 'step' : undefined}
                className="group pointer-events-auto relative flex h-6 w-6 items-center justify-center cursor-pointer"
              >
                <span
                  className={`flex h-6 w-6 items-center justify-center rounded-full border font-data text-[10px] font-semibold transition-all duration-300 ${state}`}
                >
                  {step.num}
                </span>
                <span className="pointer-events-none absolute right-full top-1/2 mr-2 -translate-y-1/2 whitespace-nowrap rounded-full border border-primary-300 bg-background-50 px-2.5 py-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                  <span className="font-data text-[10px] tracking-[0.18em] text-primary-700">
                    {step.num}
                  </span>
                  <span className="ml-2 font-heading text-[11px] font-semibold text-foreground-900">
                    {step.label}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Compact bar for smaller screens */}
      <div
        className={`fixed inset-x-0 top-[76px] z-30 transition-all duration-500 lg:hidden ${
          inRange ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-2 opacity-0'
        }`}
      >
        <div className="mx-auto max-w-[1440px] px-4 md:px-8">
          <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50/95 px-3.5 py-2.5 backdrop-blur-md">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-primary-300 bg-primary-50 font-data text-[11px] font-semibold text-primary-700">
              {current.num}
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-data tracking-[0.22em] text-foreground-400">
                CURRENT STEP
              </p>
              <p className="truncate font-heading text-[13px] font-semibold text-foreground-900">
                {current.label}
              </p>
            </div>
            <span className="shrink-0 font-data text-[10px] tracking-widest text-foreground-400">
              {current.num} / {STEPS[LAST].num}
            </span>
          </div>
          <div className="mt-1.5 h-0.5 w-full overflow-hidden rounded-full bg-background-200">
            <div
              className="h-full rounded-full bg-primary-500 transition-all duration-500"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default StepTracker;